import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Footer from "../components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [bandFilter, setBandFilter] = useState("");

  useEffect(() => {
    document.title = "Jigsaw Deal Room – Admin Dashboard";
    init();
  }, []);

  useEffect(() => {
    if (user) {
      loadSubmissions();
    }
  }, [statusFilter, bandFilter, user]);

  const init = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
      });
      if (response.data.ok) {
        if (response.data.user.role !== "admin") {
          navigate("/deal");
          return;
        }
        setUser(response.data.user);
      }
    } catch (err) {
      console.error("Auth check failed:", err);
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissions = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      if (bandFilter) params.append("band", bandFilter);

      const response = await axios.get(
        `${API}/admin/submissions?${params.toString()}`,
        { withCredentials: true }
      );

      if (response.data.ok) {
        setSubmissions(response.data.submissions);
      }
    } catch (err) {
      console.error("Failed to load submissions:", err);
      if (err.response?.status === 403) {
        navigate("/");
      }
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate("/");
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  const getScoreBadgeColor = (band) => {
    switch (band) {
      case "serious":
        return "bg-green-500/20 text-green-300 border-green-500/40";
      case "borderline":
        return "bg-yellow-500/20 text-yellow-300 border-yellow-500/40";
      case "tourist":
        return "bg-red-500/20 text-red-300 border-red-500/40";
      default:
        return "bg-slate-500/20 text-slate-300 border-slate-500/40";
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "submitted":
        return "bg-blue-500/20 text-blue-300 border-blue-500/40";
      case "reviewed":
        return "bg-purple-500/20 text-purple-300 border-purple-500/40";
      case "draft":
        return "bg-slate-500/20 text-slate-300 border-slate-500/40";
      default:
        return "bg-slate-500/20 text-slate-300 border-slate-500/40";
    }
  };

  const formatSlot = (slot) => {
    if (!slot) return "Not selected";
    try {
      return new Date(slot).toLocaleString("en-GB", {
        weekday: "short",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Europe/London"
      });
    } catch {
      return slot;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-white text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white flex flex-col relative">
      {/* Background Pattern */}
      <div 
        className="fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_dealauth-api/artifacts/z6ik3546_Subtle%20Pattern%20On%20Blue.png)',
        }}
      />
      
      {/* Side Fade Overlays */}
      <div className="fixed inset-0 bg-gradient-to-r from-slate-900/60 via-transparent to-slate-900/60 pointer-events-none" />
      
      {/* Header */}
      <header className="relative z-10 border-b border-white/10 bg-slate-900/30 backdrop-blur-md sticky top-0">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <a 
                href="https://www.peakassetinvestments.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_dealauth-api/artifacts/ff1ei5kf_RGB_Motif%20Small%20Full%20Colour%20White.png"
                  alt="Peak Asset Investments"
                  className="h-8 w-auto"
                />
              </a>
              <div className="h-6 w-px bg-white/20 mx-2" />
              <div>
                <div className="text-sm font-semibold">
                  Jigsaw Conferences Ltd – Deal Room (Admin)
                </div>
                <div className="text-xs text-slate-400">Submissions, Scoring & Decisions</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-xs text-slate-400">{user?.email}</div>
              <button
                onClick={handleLogout}
                className="text-xs text-slate-300 hover:text-white transition px-3 py-1.5 rounded-lg border border-white/10 hover:border-white/20"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Buyer Submissions</h1>
          <p className="text-slate-400">
            Review and manage all buyer qualifications
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full rounded-xl bg-slate-800/50 border border-slate-600 px-4 py-2 text-sm text-white"
              >
                <option value="">All Status</option>
                <option value="draft">Draft</option>
                <option value="submitted">Submitted</option>
                <option value="reviewed">Reviewed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Score Band
              </label>
              <select
                value={bandFilter}
                onChange={(e) => setBandFilter(e.target.value)}
                className="w-full rounded-xl bg-slate-800/50 border border-slate-600 px-4 py-2 text-sm text-white"
              >
                <option value="">All Bands</option>
                <option value="serious">Serious</option>
                <option value="borderline">Borderline</option>
                <option value="tourist">Tourist</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10 bg-slate-800/50">
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">
                    Buyer Email
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">
                    Score
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">
                    Band
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">
                    Slot
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-4"></th>
                </tr>
              </thead>
              <tbody>
                {submissions.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="px-6 py-12 text-center text-slate-400"
                    >
                      No submissions found
                    </td>
                  </tr>
                ) : (
                  submissions.map((sub) => (
                    <tr
                      key={sub.id}
                      className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition"
                      onClick={() => navigate(`/admin/submission/${sub.id}`)}
                    >
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-white">
                          {sub.buyer_email}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadgeColor(
                            sub.status
                          )}`}
                        >
                          {sub.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-semibold text-white">
                          {sub.score}/100
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-xs font-medium uppercase border ${getScoreBadgeColor(
                            sub.score_band
                          )}`}
                        >
                          {sub.score_band}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-xs text-slate-300">
                          {formatSlot(sub.slot)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-xs text-slate-400">
                          {formatDate(sub.submitted_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button className="text-cyan-400 hover:text-cyan-300 text-sm font-medium">
                          View →
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary */}
        <div className="mt-6 text-sm text-slate-400">
          Showing {submissions.length} submission{submissions.length !== 1 ? "s" : ""}
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default AdminDashboard;